import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { activePeriods, type StoryModel, visibleRange } from './story-model';
import {
  applyStoryTransforms,
  blendPeriodTint,
  dayKey,
  formatStoryDateTime,
  formatStoryDay,
  periodAccentRgb,
  STORY_VISIBLE_RADIUS,
} from './story-dom';
import type { TimelinePositionEngine } from './use-timeline-position';

interface StoryRenderWindowOptions {
  readonly model: StoryModel;
  readonly engine: TimelinePositionEngine;
  readonly cardsRef: RefObject<HTMLDivElement | null>;
  readonly dateLabelRef: RefObject<HTMLElement | null>;
  readonly activePeriodLabelRef: RefObject<HTMLElement | null>;
  readonly stageSurfaceRef: RefObject<HTMLElement | null>;
  readonly liveDateRef: RefObject<HTMLElement | null>;
  readonly reducedMotion: boolean;
}

interface VisibleWindow {
  readonly start: number;
  readonly end: number;
}

export function useStoryRenderWindow({
  model,
  engine,
  cardsRef,
  dateLabelRef,
  activePeriodLabelRef,
  stageSurfaceRef,
  liveDateRef,
  reducedMotion,
}: StoryRenderWindowOptions): VisibleWindow {
  const [windowRange, setWindowRange] = useState<VisibleWindow>(() =>
    visibleRange(model, engine.get(), STORY_VISIBLE_RADIUS),
  );
  const modelRef = useRef(model);
  const rangeRef = useRef(windowRange);
  const reducedMotionRef = useRef(reducedMotion);
  const lastLiveDayRef = useRef('');

  useEffect(() => {
    modelRef.current = model;
    const reset = visibleRange(model, engine.get(), STORY_VISIBLE_RADIUS);
    rangeRef.current = reset;
    setWindowRange(reset);
    applyStoryTransforms(cardsRef.current, engine.get(), reducedMotionRef.current);
  }, [cardsRef, engine, model, reducedMotion]);

  useEffect(() => {
    reducedMotionRef.current = reducedMotion;
  }, [reducedMotion]);

  useEffect(() => {
    const syncDom = (position: number) => {
      const next = visibleRange(modelRef.current, position, STORY_VISIBLE_RADIUS);
      if (next.start !== rangeRef.current.start || next.end !== rangeRef.current.end) {
        rangeRef.current = next;
        setWindowRange(next);
      }
      const epoch = modelRef.current.scale.toTime(position);
      const day = dayKey(epoch);
      if (dateLabelRef.current) dateLabelRef.current.textContent = formatStoryDateTime(epoch);
      const currentPeriods = activePeriods(modelRef.current, position);
      if (activePeriodLabelRef.current) {
        activePeriodLabelRef.current.textContent =
          currentPeriods.length === 0
            ? ''
            : currentPeriods.map((period) => period.period.label).join(' · ');
        stylePeriodChip(
          activePeriodLabelRef.current,
          currentPeriods.map((period) => period.period.color),
        );
      }
      if (stageSurfaceRef.current) {
        const tint = blendPeriodTint(
          currentPeriods.map((period) => period.period.color),
          reducedMotionRef.current,
        );
        stageSurfaceRef.current.style.background =
          tint === 'transparent'
            ? 'var(--color-surface-raised)'
            : `radial-gradient(circle at 72% 48%, ${tint} 0%, rgba(0, 0, 0, 0) 70%), var(--color-surface-raised)`;
      }
      if (liveDateRef.current && day !== lastLiveDayRef.current) {
        lastLiveDayRef.current = day;
        liveDateRef.current.textContent = formatStoryDay(epoch);
      }
      applyStoryTransforms(cardsRef.current, position, reducedMotionRef.current);
    };

    const unsubscribe = engine.subscribe(syncDom);
    syncDom(engine.get());
    return unsubscribe;
  }, [activePeriodLabelRef, cardsRef, dateLabelRef, engine, liveDateRef, stageSurfaceRef]);

  return windowRange;
}

function stylePeriodChip(node: HTMLElement, colors: readonly string[]): void {
  if (colors.length === 0) {
    node.style.display = 'none';
    node.style.backgroundColor = '';
    node.style.borderColor = '';
    node.style.color = '';
    node.style.boxShadow = '';
    return;
  }
  node.style.display = 'inline-flex';
  const [r, g, b] = periodAccentRgb(colors);
  node.style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.18)`;
  node.style.borderColor = `rgba(${r}, ${g}, ${b}, 0.52)`;
  node.style.color = `rgb(${r}, ${g}, ${b})`;
  node.style.boxShadow = `0 0 0 1px rgba(${r}, ${g}, ${b}, 0.14)`;
}
